import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * A store for in-progress edits that OUTLIVES the element body's remount (ADR-044 §11).
 *
 * **Why this exists.** The WebGL panel keys `StepArElementBody` by element id (D14) so a draft typed on element A
 * can never appear on element B — measured, because the draft hooks retire only when the SERVER value moves, and
 * with both elements holding an empty string the draft leaked and the next blur committed A's text onto B. Unity
 * drives those selection changes over the bridge, so the same key silently destroys whatever the author had typed
 * (D22): they click Unity's canvas, Unity selects a sibling, and the text is gone with no message.
 *
 * **Why not just commit the draft first.** Because the selection arrives as an inbound Unity message, and
 * ADR-049 D2 forbids an inbound message from causing a request to leave the browser. Flushing would trade a
 * data-loss bug for a hole in the invariant that makes the whole bridge safe. So the draft is PRESERVED instead:
 * nothing is written, nothing is sent.
 *
 * **Scoped per element AND per setting**, which is what lets this coexist with D14 rather than undo it: element
 * B still never sees A's draft, and returning to A restores A's own.
 */
interface DraftScopeValue {
  /** The element the mounted body belongs to. Part of every slot key. */
  id: string;
  /** Slot to last state. A plain Map in a ref: reading it is how a remount restores, and it needs no re-render. */
  store: Map<string, unknown>;
}

const DraftScopeContext = createContext<DraftScopeValue | null>(null);

/**
 * Provide a draft store for everything below.
 *
 * **Must be mounted ABOVE the element `key`** — that is the entire point. Inside it, the store dies with the
 * subtree it was meant to outlive.
 */
export function DraftScope({ id, children }: { id: number | string; children: ReactNode }) {
  const store = useRef<Map<string, unknown>>(new Map()).current;
  const value = useMemo<DraftScopeValue>(() => ({ id: String(id), store }), [id, store]);
  return <DraftScopeContext.Provider value={value}>{children}</DraftScopeContext.Provider>;
}

/**
 * `useState`, except the initial value is restored from the enclosing {@link DraftScope} when there is one.
 *
 * **No scope means plain `useState`, byte-for-byte the previous behaviour** (D27). The main editor mounts no
 * provider, so it is unchanged by construction; opting it in is a separate decision with its own verification.
 *
 * Every write goes through this setter, including the draft hooks' RETIREMENT path — which is required, not
 * incidental (D26). The hooks discard a draft whenever the server value moves; if that reset did not reach the
 * store, a remount would restore a draft the server had already invalidated, resurrecting exactly the stale value
 * an earlier review round removed.
 */
export function useScopedDraftState<T>(key: string, initial: T): [T, (next: T) => void] {
  const scope = useContext(DraftScopeContext);
  const slot = scope ? `${scope.id} ${key}` : null;

  const [state, setState] = useState<T>(() =>
    slot !== null && scope !== null && scope.store.has(slot) ? (scope.store.get(slot) as T) : initial,
  );

  // NOT `useCallback`: the draft hooks call this during render on the retirement path, and a memoised setter
  // whose deps include `slot` would be rebuilt there anyway. Writing to the Map is a cache write, idempotent
  // under StrictMode's double invoke.
  const set = (next: T): void => {
    if (slot !== null && scope !== null) scope.store.set(slot, next);
    setState(next);
  };

  return [state, set];
}
